// Net: ledninger, stationer, priser, kapacitet og N-1-skjolde.

import { cableHalved, heatStrength, heldLine, smaaHalved } from './effects';
import type { GridNode, Line, LineMedium, LineTier, NodeKind, State } from './types';

// --- Tal fra DESIGN.md "Tal: ledninger" -------------------------------------------------------

export const CAP_MW: Record<LineTier, number> = { 1: 300, 2: 1200, 3: 2400 };
export const SEA_CAP_MW: Record<1 | 2, number> = { 1: 300, 2: 1200 };
export const HVDC_UPGRADE_COST = 30;
export const REINFORCE_COST = 2;
export const STATION_COST = 10;
/** Stationens pris nu: gratis, mens gaven "Ny station" har en til gode. */
export function stationCost(state: { perks: { freeStation?: number } }): number {
  return (state.perks.freeStation ?? 0) > 0 ? 0 : STATION_COST;
}
export const CREW_COST = 8;
export const BATTERY_COST = 15;
export const FLYWHEEL_COST = 18;
export const BATTERY_MW = 200;
export const BATTERY_SECONDS = 20;
export const BIG_BATTERY_MW = 400;
export const SLOTS = [3, 6] as const;
export const MAX_LAND_KM = 140;
export const MAX_SEA_KM = 130;
/** Fortryd: nedrivning inden for 3 s giver fuld refusion. */
export const UNDO_TICKS = 30;
/**
 * Nedrivning efter fortryd-vinduet giver en fjerdedel tilbage (skrotværdi). Med 50 % føltes det som at
 * få pengene igen (Iason, playtest okt. 2026); en kvart gør en fejlinvestering til et reelt tab, men en
 * ledning, der står i vejen for en ny, kan stadig rives ned for en slat.
 */
export const DEMOLISH_SHARE = 0.25;
/** En luftledning nærmere end 8 km fra en by kan udløse naboprotest. */
export const PROTEST_KM = 8;

/** Reparationstid i ticks med montør på stedet. */
export function repairTicks(line: Line): number {
  if (line.medium === 'sea' || line.medium === 'hvdc') return 200;
  const base = line.tier === 1 ? 60 : 80;
  return line.medium === 'cable' ? base * 3 : base;
}

/** Montørbilens fart i km pr. tick (25 km/s). Skibet sejler 15 km/s. */
export const VAN_KM_PER_TICK = 2.5;
export const SHIP_KM_PER_TICK = 1.5;

// --- Opslag -------------------------------------------------------------------------------------

export function nodeById(state: State, id: string): GridNode | undefined {
  for (const n of state.nodes) if (n.id === id) return n;
  return undefined;
}

export function lineById(state: State, id: string): Line | undefined {
  for (const l of state.lines) if (l.id === id) return l;
  return undefined;
}

export function lineBetween(state: State, a: string, b: string): Line | undefined {
  for (const l of state.lines) {
    if ((l.a === a && l.b === b) || (l.a === b && l.b === a)) return l;
  }
  return undefined;
}

/** Fast nøgle for et nodepar, uafhængigt af rækkefølgen. */
export function pairKey(a: string, b: string): string {
  return a < b ? `${a}|${b}` : `${b}|${a}`;
}

export function dist(a: { x: number; y: number }, b: { x: number; y: number }): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
}

export function lineMid(state: State, line: Line): { x: number; y: number } {
  const a = nodeById(state, line.a);
  const b = nodeById(state, line.b);
  if (!a || !b) return { x: 0, y: 0 };
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

// --- Kapacitet ----------------------------------------------------------------------------------

export function isForeignLine(state: State, line: Line): boolean {
  const a = nodeById(state, line.a);
  const b = nodeById(state, line.b);
  return a?.kind === 'foreign' || b?.kind === 'foreign';
}

/** Nominel kapacitet i MW, når ledningen er hel. */
export function nominalCap(state: State, line: Line): number {
  const cap = baseCap(state, line);
  // En småt-gæst på ledningen eller en kabelskade: den bærer det halve, til det er overstået.
  return smaaHalved(state, line.id) || cableHalved(state, line.id) ? Math.round(cap / 2) : cap;
}

function baseCap(state: State, line: Line): number {
  if (line.fixedCapMw > 0) {
    const boost = isForeignLine(state, line) ? state.perks.foreignBoostPct : 0;
    return Math.round((line.fixedCapMw * (100 + boost)) / 100);
  }
  if (line.medium === 'sea') return SEA_CAP_MW[line.tier === 1 ? 1 : 2];
  let cap = CAP_MW[line.tier];
  if (line.halfDown) cap = Math.round(cap / 2);
  // Hedebølge: varme luftledninger hænger mere og må bære mindre.
  if (line.medium === 'air') cap = Math.round((cap * heatAirPm(heatStrength(state))) / 1000);
  return cap;
}

/** Luftledningers kapacitet i promille under en hedebølge. */
export const HEAT_AIR_PM = 750;
/**
 * Den milde hedebølge (styrke 1). Spor "sværhed": på bane 1 er alle ledninger luft, og 75 % væltede
 * hele nettet på de hårde niveauer, så banen blev sværere end bane 3.
 */
export const HEAT_AIR_MILD_PM = 900;

/** Luftkapacitet i promille for en hedebølge af en given styrke (0 = ingen hedebølge). */
export function heatAirPm(strength: number): number {
  return strength <= 0 ? 1000 : strength === 1 ? HEAT_AIR_MILD_PM : HEAT_AIR_PM;
}

/**
 * Kapacitet lige nu: 0 hvis brudt. Et borgermøde halverer ledningen, mens mødet kører. Det slukkede
 * den før helt, og som standardsvar kappede det så den eneste vej ind til en ny by (playtest runde 2).
 */
export function lineCap(state: State, line: Line): number {
  if (line.status !== 'ok') return 0;
  const cap = nominalCap(state, line);
  return heldLine(state, line.id) ? Math.round(cap / 2) : cap;
}

// --- Priser -------------------------------------------------------------------------------------

function kmPart(km: number): number {
  return Math.ceil(km / 10);
}

export function airBuildCost(km: number): number {
  return 3 + kmPart(km);
}

export function mediumFactor(medium: LineMedium): number {
  if (medium === 'cable') return 3;
  if (medium === 'sea') return 4;
  return 1;
}

/** Hvad en luftledning af dette trin er værd i alt (bygning plus opgraderinger). */
export function airValue(tier: LineTier, km: number): number {
  let v = airBuildCost(km);
  if (tier >= 2) v += 6 + kmPart(km);
  if (tier >= 3) v += 10 + kmPart(km);
  return v;
}

function discounted(state: State, medium: LineMedium, cost: number): number {
  if ((medium === 'cable' || medium === 'sea') && state.perks.cableDiscountPct > 0) {
    return Math.max(1, Math.round((cost * (100 - state.perks.cableDiscountPct)) / 100));
  }
  return cost;
}

export function buildCost(state: State, medium: LineMedium, km: number): number {
  if (medium === 'air' && state.perks.freeAir > 0) return 0;
  return discounted(state, medium, airBuildCost(km) * mediumFactor(medium));
}

/** Pris for at opgradere til næste trin, eller null hvis det ikke kan lade sig gøre. */
export function upgradeCost(state: State, line: Line): number | null {
  if (line.medium === 'hvdc') {
    if (line.fixedCapMw >= 1200 || isForeignLine(state, line)) return null;
    return HVDC_UPGRADE_COST;
  }
  if (line.fixed) return null;
  if (line.tier === 3) return null;
  if (line.medium === 'sea' && line.tier >= 2) return null;
  if (line.tier === 2 && state.perks.freeDouble > 0 && line.medium !== 'sea') return 0;
  const base = line.tier === 1 ? 6 + kmPart(line.km) : 10 + kmPart(line.km);
  return discounted(state, line.medium, base * mediumFactor(line.medium));
}

/** Pris for at grave en luftledning ned. Kabel koster 3x, så forskellen er 2x luftværdien. */
export function buryCost(state: State, line: Line): number | null {
  if (line.medium !== 'air' || line.fixed) return null;
  return discounted(state, 'cable', airValue(line.tier, line.km) * 2);
}

export function demolishRefund(state: State, line: Line): number {
  if (state.tick - line.builtTick <= UNDO_TICKS) {
    // Fortryd: alt tilbage, men tilslutningsbetalingen, ledningen udløste, går samme vej retur.
    const fee = line.feeNode ? (nodeById(state, line.feeNode)?.producer?.fee ?? 0) : 0;
    return line.spent - fee;
  }
  return Math.floor(line.spent * DEMOLISH_SHARE);
}

// --- Pladser og regler for nye ledninger -----------------------------------------------------------

export function slotsUsed(state: State, nodeId: string): number {
  let n = 0;
  for (const l of state.lines) if (!l.fixed && (l.a === nodeId || l.b === nodeId)) n++;
  return n;
}

export function slotsFree(state: State, node: GridNode): number {
  if (node.kind === 'foreign') return 0;
  return SLOTS[node.station] - slotsUsed(state, node.id);
}

const SEA_ONLY: NodeKind[] = ['offshore'];

/** Hvilke medier en ny ledning mellem a og b må have. Tom = ikke tilladt. */
export function allowedMedia(_state: State, a: GridNode, b: GridNode): LineMedium[] {
  if (!a.active || !b.active) return [];
  if (a.id === b.id) return [];
  if (a.kind === 'foreign' || b.kind === 'foreign') return [];
  if (a.area !== b.area) return [];
  const offshore = SEA_ONLY.includes(a.kind) || SEA_ONLY.includes(b.kind);
  const km = dist(a, b);
  if (offshore) {
    // Søkabel kun fra kystnode til havvind (eller havvind til havvind).
    const other = SEA_ONLY.includes(a.kind) ? b : a;
    if (!(other.coast || SEA_ONLY.includes(other.kind))) return [];
    return km <= MAX_SEA_KM ? ['sea'] : [];
  }
  if (km > MAX_LAND_KM) return [];
  return ['air', 'cable'];
}

export type LineCheck =
  | { ok: true; medium: LineMedium; km: number; cost: number }
  | { ok: false; error: 'unknown_target' | 'wrong_area' | 'too_far' | 'already_exists' | 'no_slots' | 'bad_option' | 'not_unlocked' };

export function checkNewLine(state: State, aId: string, bId: string, medium: LineMedium, unlocked: (m: LineMedium) => boolean): LineCheck {
  const a = nodeById(state, aId);
  const b = nodeById(state, bId);
  if (!a || !b || !a.active || !b.active || a.id === b.id) return { ok: false, error: 'unknown_target' };
  if (a.area !== b.area) return { ok: false, error: 'wrong_area' };
  if (lineBetween(state, a.id, b.id)) return { ok: false, error: 'already_exists' };
  const media = allowedMedia(state, a, b);
  if (media.length === 0) {
    if (a.kind === 'foreign' || b.kind === 'foreign') return { ok: false, error: 'unknown_target' };
    return { ok: false, error: 'too_far' };
  }
  if (!media.includes(medium)) return { ok: false, error: 'bad_option' };
  if (!unlocked(medium)) return { ok: false, error: 'not_unlocked' };
  if (slotsFree(state, a) <= 0 || slotsFree(state, b) <= 0) return { ok: false, error: 'no_slots' };
  const km = Math.round(dist(a, b) * 10) / 10;
  return { ok: true, medium, km, cost: buildCost(state, medium, km) };
}

// --- Geometri til storme ------------------------------------------------------------------------

/** Rører linjestykket a-b et vandret bånd y in [y0, y1]? */
export function segmentInBand(a: { y: number }, b: { y: number }, y0: number, y1: number): boolean {
  const lo = Math.min(a.y, b.y);
  const hi = Math.max(a.y, b.y);
  return hi >= y0 && lo <= y1;
}

/** Afstand fra punkt p til linjestykket a-b. */
export function pointSegDist(p: { x: number; y: number }, a: { x: number; y: number }, b: { x: number; y: number }): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return dist(p, a);
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  return dist(p, { x: a.x + t * dx, y: a.y + t * dy });
}

// --- N-1-skjolde --------------------------------------------------------------------------------

function isSupply(n: GridNode): boolean {
  return n.active && (n.kind === 'wind' || n.kind === 'solar' || n.kind === 'offshore' || n.kind === 'plant' || n.kind === 'foreign');
}

/**
 * Antal kantdisjunkte veje (højst 2) fra en node til nogen forsyning, kun over hele ledninger.
 * Edmonds-Karp med enhedskapacitet; grafen er lille, og rækkefølgen er fast.
 */
export function supplyPaths(state: State, nodeId: string): 0 | 1 | 2 {
  const idx = new Map<string, number>();
  state.nodes.forEach((n, i) => idx.set(n.id, i));
  const N = state.nodes.length + 1; // sidste = super-dræn
  const T = N - 1;
  const S = idx.get(nodeId);
  if (S === undefined) return 0;
  const start = state.nodes[S];
  if (!start || !start.active) return 0;
  // Residualgraf som kantliste.
  const to: number[] = [];
  const cap: number[] = [];
  const head: number[] = new Array<number>(N).fill(-1);
  const nxt: number[] = [];
  const add = (u: number, v: number, c: number): void => {
    to.push(v); cap.push(c); nxt.push(head[u] ?? -1); head[u] = to.length - 1;
    to.push(u); cap.push(0); nxt.push(head[v] ?? -1); head[v] = to.length - 1;
  };
  for (const l of state.lines) {
    if (l.status !== 'ok') continue;
    const u = idx.get(l.a);
    const v = idx.get(l.b);
    if (u === undefined || v === undefined) continue;
    // Uorienteret enhedskant: to retninger.
    add(u, v, 1);
    add(v, u, 1);
  }
  state.nodes.forEach((n, i) => {
    if (i !== S && isSupply(n)) add(i, T, 2);
  });
  let flow = 0;
  while (flow < 2) {
    const prevEdge: number[] = new Array<number>(N).fill(-1);
    const seen: boolean[] = new Array<boolean>(N).fill(false);
    const queue: number[] = [S];
    seen[S] = true;
    while (queue.length > 0 && !seen[T]) {
      const u = queue.shift() as number;
      for (let e = head[u] ?? -1; e !== -1; e = nxt[e] ?? -1) {
        const v = to[e] as number;
        if (!seen[v] && (cap[e] ?? 0) > 0) {
          seen[v] = true;
          prevEdge[v] = e;
          queue.push(v);
        }
      }
    }
    if (!seen[T]) break;
    let v = T;
    while (v !== S) {
      const e = prevEdge[v] as number;
      cap[e] = (cap[e] ?? 0) - 1;
      cap[e ^ 1] = (cap[e ^ 1] ?? 0) + 1;
      v = to[e ^ 1] as number;
    }
    flow++;
  }
  return Math.min(2, flow) as 0 | 1 | 2;
}

/** Opdatér skjolde for alle byer og datacentre. Kaldes ved netændring. */
export function updateShields(state: State): void {
  for (const n of state.nodes) {
    if (!n.active || (n.kind !== 'city' && n.kind !== 'datacenter')) {
      n.shield = 0;
      continue;
    }
    n.shield = supplyPaths(state, n.id);
  }
}

/**
 * Producenter og byer der lige har mistet deres sidste hele ledning. En by tæller kun, hvis den fik
 * strøm lige før: en by, der allerede var mørk, er ikke "skåret fra" (IGN-løkke runde 2).
 */
export function cutNodes(state: State, line: { a: string; b: string }): GridNode[] {
  const out: GridNode[] = [];
  for (const id of [line.a, line.b]) {
    const n = nodeById(state, id);
    if (!n || !n.active || n.phantom || (!n.producer && !n.city) || n.kind === 'foreign') continue;
    if (n.city && n.city.servedMw <= 0) continue;
    if (degree(state, id) === 0) out.push(n);
  }
  return out;
}

/** Ledninger der forbinder til noden (kun hele). */
export function degree(state: State, nodeId: string): number {
  let d = 0;
  for (const l of state.lines) if (l.status === 'ok' && (l.a === nodeId || l.b === nodeId)) d++;
  return d;
}

/** Noder der kan nås fra en given node over hele ledninger. */
export function reachable(state: State, from: string): Set<string> {
  const seen = new Set<string>([from]);
  const stack = [from];
  while (stack.length > 0) {
    const u = stack.pop() as string;
    for (const l of state.lines) {
      if (l.status !== 'ok') continue;
      const v = l.a === u ? l.b : l.b === u ? l.a : null;
      if (v !== null && !seen.has(v)) {
        seen.add(v);
        stack.push(v);
      }
    }
  }
  return seen;
}
